import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState(localStorage.getItem('session_token'));

  useEffect(() => {
    if (sessionToken) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [sessionToken]);

  const fetchCurrentUser = async () => {
    try {
      console.log('Fetching current user with token:', sessionToken);
      const response = await axios.get(`${API}/auth/me`, {
        headers: {
          Authorization: `Bearer ${sessionToken}`
        }
      });

      console.log('User fetch response:', response.data);

      if (response.data.success) {
        setUser(response.data.user);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      console.error('Error response:', error.response?.data);
      // Only logout if it's an auth error
      if (error.response?.status === 401) {
        logout();
      } else {
        setLoading(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      console.log('Attempting login for:', email);
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });

      console.log('Login response:', response.data);

      if (response.data.success) {
        const token = response.data.session_token;
        localStorage.setItem('session_token', token);
        setSessionToken(token);
        setUser(response.data.user);
        return { success: true };
      }
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error details:', error.response?.data);
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const signup = async (name, email, password) => {
    try {
      const response = await axios.post(`${API}/auth/signup`, {
        name,
        email,
        password
      });

      if (response.data.success) {
        const token = response.data.session_token;
        localStorage.setItem('session_token', token);
        setSessionToken(token);
        setUser(response.data.user);
        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Signup failed'
      };
    }
  };

  const loginWithGoogle = async (sessionId) => {
    try {
      console.log('Attempting Google login with session_id...');
      const formData = new FormData();
      formData.append('session_id', sessionId);

      const response = await axios.post(`${API}/auth/google`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Google login response:', response.data);

      if (response.data.success) {
        const token = response.data.session_token;
        localStorage.setItem('session_token', token);
        setSessionToken(token);
        setUser(response.data.user);
        return { success: true };
      } else {
        return {
          success: false,
          error: 'Google login failed - invalid response'
        };
      }
    } catch (error) {
      console.error('Google login error:', error);
      console.error('Error response:', error.response?.data);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Google login failed'
      };
    }
  };

  const logout = async () => {
    try {
      if (sessionToken) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: {
            Authorization: `Bearer ${sessionToken}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('session_token');
      setSessionToken(null);
      setUser(null);
    }
  };

  const refreshUser = async () => {
    if (sessionToken) {
      await fetchCurrentUser();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        sessionToken,
        login,
        signup,
        loginWithGoogle,
        logout,
        refreshUser,
        isAuthenticated: !!user
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
